/**
 * PsychometricBaselineNudge — subtle top-of-dashboard chip that reminds
 * signed-in users to finish the 5-minute psychometric baseline while they
 * poke around. Auto-hides for anyone who has already recorded a
 * `psychometric_composite`. Dismissible for the current session so it
 * doesn't nag on every dashboard reload.
 *
 * Renders a **small radial progress ring** on the left that fills as the
 * user answers each of the 20 traits — sourced from the localStorage draft
 * blob written by `pages/PsychometricOnboarding.jsx`.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { hasInstitutionalFeatures } from "@/lib/tier";
import { PSYCHOMETRIC_DRAFT_KEY, PSYCHOMETRIC_TOTAL } from "@/pages/PsychometricOnboarding";

const DISMISS_KEY = "md_baseline_nudge_dismissed_at";
const DISMISS_TTL_HOURS = 12;   // re-show after half a day
const RING_SIZE = 44;           // px
const RING_STROKE = 4;          // px

// Same localisation table as backend Kimani CTA — keeps the two nudges consistent.
const LABELS = {
    EN: { title: "Finish your 5-min psychometric baseline",
          sub:   "Unlocks personalised coaching prompts + your Behavioral Alpha.",
          cta:   "Start",
          resume:"Resume",
          progress: (a, t) => `${a} of ${t} traits done` },
    SW: { title: "Kamilisha tathmini ya dakika 5",
          sub:   "Ufungue mafunzo binafsi + Behavioral Alpha yako.",
          cta:   "Anza",
          resume:"Endelea",
          progress: (a, t) => `${a} kati ya ${t} zimekamilika` },
    SH: { title: "Kamilisha tathmini ya dakika 5",
          sub:   "Ufungue mafunzo binafsi + Behavioral Alpha yako.",
          cta:   "Anza",
          resume:"Endelea",
          progress: (a, t) => `${a}/${t} tabia zimekamilika` },
    FR: { title: "Terminez votre bilan psychométrique (5 min)",
          sub:   "Débloquez le coaching personnalisé + votre Behavioral Alpha.",
          cta:   "Commencer",
          resume:"Reprendre",
          progress: (a, t) => `${a} sur ${t} traits complétés` },
    AR: { title: "أكمل التقييم النفسي (٥ دقائق)",
          sub:   "افتح إرشادًا مخصصًا + Behavioral Alpha الخاص بك.",
          cta:   "ابدأ",
          resume:"استئناف",
          progress: (a, t) => `${a} من ${t} سمات مكتملة` },
    PT: { title: "Complete sua avaliação psicométrica (5 min)",
          sub:   "Desbloqueie coaching personalizado + seu Behavioral Alpha.",
          cta:   "Começar",
          resume:"Retomar",
          progress: (a, t) => `${a} de ${t} traços concluídos` },
};

// Cheap counter that reads the draft blob written by the onboarding wizard.
function readDraftCount() {
    try {
        const raw = localStorage.getItem(PSYCHOMETRIC_DRAFT_KEY);
        if (!raw) return 0;
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
            return Object.keys(parsed).length;
        }
    } catch { /* ignore */ }
    return 0;
}

export default function PsychometricBaselineNudge({ locked = false }) {
    const { user } = useAuth();
    const [dismissed, setDismissed] = useState(false);
    const [answered, setAnswered]   = useState(0);

    // Watch localStorage on mount + at 3-second poll so the ring fills as the
    // user answers questions in another tab or immediately after a session
    // resumes. Cheap since the draft is < 200 bytes.
    useEffect(() => {
        setAnswered(readDraftCount());
        const id = setInterval(() => setAnswered(readDraftCount()), 3000);
        // Cross-tab sync via the storage event.
        const onStorage = (evt) => {
            if (evt.key === PSYCHOMETRIC_DRAFT_KEY) setAnswered(readDraftCount());
        };
        window.addEventListener("storage", onStorage);
        return () => { clearInterval(id); window.removeEventListener("storage", onStorage); };
    }, []);

    useEffect(() => {
        if (locked) return; // locked = mandatory step, dismiss state irrelevant
        try {
            const raw = localStorage.getItem(DISMISS_KEY);
            if (!raw) return;
            const ts = parseInt(raw, 10);
            if (Number.isFinite(ts) && Date.now() - ts < DISMISS_TTL_HOURS * 3600 * 1000) {
                setDismissed(true);
            }
        } catch { /* ignore */ }
    }, [locked]);

    // Show only when signed-in + on a tier that actually has access (advanced /
    // institutional / admin — mirrors the backend `is_institutional_lite` gate)
    // + baseline not yet done + not dismissed this window. Free-tier users must
    // NOT see this chip — its CTA used to dead-end on the paywall screen, which
    // is exactly what read as "the baseline is still paywalled".
    if (!user) return null;
    if (!hasInstitutionalFeatures(user)) return null;
    if (user.psychometric_composite != null) return null;
    if (!locked && dismissed) return null;

    const lang = (user.preferred_language || "EN").toUpperCase();
    const isRtl = lang === "AR";
    const copy = LABELS[lang] || LABELS.EN;
    const isResume = answered > 0;
    const pct = Math.min(1, answered / PSYCHOMETRIC_TOTAL);

    // Radial progress ring geometry.
    const radius = (RING_SIZE - RING_STROKE) / 2;
    const circ   = 2 * Math.PI * radius;
    const dash   = circ * pct;

    function onDismiss() {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
        setDismissed(true);
    }

    return (
        <div
            data-testid="psychometric-baseline-nudge"
            data-progress={answered}
            dir={isRtl ? "rtl" : undefined}
            className="mb-3 flex items-center gap-3 rounded-xl border border-primary/40 bg-primary/5 px-3.5 py-2.5 text-sm"
        >
            {/* Radial progress ring — fills as user answers */}
            <div
                className="relative shrink-0"
                style={{ width: RING_SIZE, height: RING_SIZE }}
                data-testid="psychometric-baseline-nudge-progress"
                aria-label={copy.progress(answered, PSYCHOMETRIC_TOTAL)}
                title={copy.progress(answered, PSYCHOMETRIC_TOTAL)}
            >
                <svg
                    width={RING_SIZE}
                    height={RING_SIZE}
                    viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
                    className="-rotate-90"
                >
                    <circle
                        cx={RING_SIZE / 2}
                        cy={RING_SIZE / 2}
                        r={radius}
                        stroke="currentColor"
                        strokeWidth={RING_STROKE}
                        fill="none"
                        className="text-primary/20"
                    />
                    <circle
                        cx={RING_SIZE / 2}
                        cy={RING_SIZE / 2}
                        r={radius}
                        stroke="currentColor"
                        strokeWidth={RING_STROKE}
                        strokeLinecap="round"
                        fill="none"
                        className="text-primary transition-[stroke-dasharray] duration-500 ease-out"
                        strokeDasharray={`${dash} ${circ}`}
                    />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center text-[10px] font-black mono proportional-nums text-primary">
                    {answered}/{PSYCHOMETRIC_TOTAL}
                </div>
            </div>

            <div className="flex-1 min-w-0">
                <div className="font-semibold text-foreground leading-tight">{copy.title}</div>
                <div className="text-xs text-muted-foreground leading-tight mt-0.5">
                    {isResume ? copy.progress(answered, PSYCHOMETRIC_TOTAL) : copy.sub}
                </div>
            </div>
            <Link
                to="/onboarding/psychometric"
                data-testid="psychometric-baseline-nudge-cta"
                className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-3 py-1.5 text-xs font-semibold hover:opacity-90 transition-opacity shrink-0"
            >
                {isResume ? copy.resume : copy.cta}
                <ArrowRight className={`w-3 h-3 ${isRtl ? "rotate-180" : ""}`} />
            </Link>
            {!locked && (
                <button
                    type="button"
                    aria-label="Dismiss reminder"
                    data-testid="psychometric-baseline-nudge-dismiss"
                    onClick={onDismiss}
                    className="text-muted-foreground/60 hover:text-foreground transition-colors shrink-0"
                >
                    <X className="w-4 h-4" />
                </button>
            )}
        </div>
    );
}
